import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { AppSettings, SettingsPatch } from '../api/types';
import { Card, PageHeader } from '../components/Card';
import { ErrorBanner, LoadingBlock } from '../components/Feedback';
import { Button, Field, Input, Toggle } from '../components/Form';
import { useToast } from '../components/Toast';
import { money, num } from '../lib/format';

type GroupDef = {
  key: keyof AppSettings;
  title: string;
  hint: string;
  fields: Array<{ key: string; label: string; type: 'number' | 'boolean' | 'micro' }>;
};

const GROUPS: GroupDef[] = [
  {
    key: 'limits',
    title: 'Лимиты запросов',
    hint: 'Ограничения ниже документированных upstream caps (20/min для buy-create), чтобы не получить бан на 15 минут.',
    fields: [
      { key: 'buyCreatePerMin', label: 'Buy/create в минуту', type: 'number' },
      { key: 'buyPrefixPerMin', label: 'Buy/prefix в минуту', type: 'number' },
      { key: 'appListPerMin', label: 'App list в минуту', type: 'number' },
      { key: 'defaultPerMin', label: 'Default в минуту', type: 'number' },
      { key: 'recordPerMin', label: 'Record в минуту', type: 'number' },
    ],
  },
  {
    key: 'queue',
    title: 'Очередь',
    hint: 'Batch window — сколько группа ждёт соседей до отправки одним заказом. Deadline — макс. время жизни задачи.',
    fields: [
      { key: 'batchWindowMs', label: 'Batch window (мс)', type: 'number' },
      { key: 'maxBatchSize', label: 'Макс. размер батча', type: 'number' },
      { key: 'jobDeadlineSeconds', label: 'Deadline задачи (с)', type: 'number' },
      { key: 'maxAttempts', label: 'Макс. попыток', type: 'number' },
      { key: 'retryBackoffMs', label: 'Backoff retry (мс)', type: 'number' },
      { key: 'reconcileAfterMs', label: 'Reconcile через (мс)', type: 'number' },
    ],
  },
  {
    key: 'pricing',
    title: 'Ценообразование',
    hint: 'Значения с суффиксом Micro — в micro-USD (1 USD = 1 000 000). Наценка применяется к себестоимости.',
    fields: [
      { key: 'markupPercent', label: 'Наценка (%)', type: 'number' },
      { key: 'markupFixedMicro', label: 'Фикс. наценка (micro)', type: 'micro' },
      { key: 'minPriceMicro', label: 'Мин. цена (micro)', type: 'micro' },
      { key: 'roundToMicro', label: 'Округление до (micro)', type: 'micro' },
    ],
  },
  {
    key: 'sms',
    title: 'SMS-поллинг',
    hint: 'Интервал и окно опроса upstream для получения кодов. Фоновый обход аренды находит коды, когда никто не ждёт на экране. autoRefund — авто-возврат при отсутствии кода.',
    fields: [
      { key: 'pollIntervalMs', label: 'Интервал опроса (мс)', type: 'number' },
      { key: 'pollWindowMinutes', label: 'Окно опроса (мин)', type: 'number' },
      { key: 'rentalPollIntervalMs', label: 'Фоновый обход аренды (мс)', type: 'number' },
      { key: 'maxConcurrentPolls', label: 'Макс. параллельных опросов', type: 'number' },
      { key: 'autoRefundMinutes', label: 'Авто-возврат через (мин, 0=выкл)', type: 'number' },
    ],
  },
  {
    key: 'catalog',
    title: 'Каталог',
    hint: 'Интервал авто-синхронизации каталога с upstream. autoEnable — автоматическое включение новых проектов.',
    fields: [
      { key: 'syncIntervalSeconds', label: 'Интервал синхр. (с)', type: 'number' },
      { key: 'autoEnableNewProjects', label: 'Авто-включение новых', type: 'boolean' },
    ],
  },
  {
    key: 'pool',
    title: 'Пул',
    hint: 'Предзаготовленные номера. maxAge — макс. возраст номера в пуле.',
    fields: [
      { key: 'enabled', label: 'Включён', type: 'boolean' },
      { key: 'maxAgeMinutes', label: 'Макс. возраст (мин)', type: 'number' },
    ],
  },
  {
    key: 'spend',
    title: 'Лимиты расхода',
    hint: 'Гарантии от перерасхода upstream. При превышении создание батчей останавливается.',
    fields: [
      { key: 'hourlyLimitUsd', label: 'Часовой лимит ($)', type: 'number' },
      { key: 'dailyLimitUsd', label: 'Дневной лимит ($)', type: 'number' },
    ],
  },
  {
    key: 'publicApi',
    title: 'Публичный API',
    hint: 'Настройки внешнего API для клиентов: rate limit, max count, heartbeat для SSE.',
    fields: [
      { key: 'defaultRateLimitPerMin', label: 'Rate limit / мин', type: 'number' },
      { key: 'maxCountPerRequest', label: 'Max count / запрос', type: 'number' },
      { key: 'streamHeartbeatMs', label: 'SSE heartbeat (мс)', type: 'number' },
    ],
  },
];

export function SettingsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const path = '/admin-api/settings';
  const query = useQuery<AppSettings, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get<AppSettings>(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const [draft, setDraft] = useState<AppSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (query.data) setDraft(structuredClone(query.data));
  }, [query.data]);

  const setField = (group: keyof AppSettings, key: string, value: number | boolean) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, [group]: { ...prev[group], [key]: value } };
    });
  };

  const save = async () => {
    if (!draft) return;
    const patch: SettingsPatch = {};
    for (const group of GROUPS) {
      const original = query.data?.[group.key];
      const current = draft[group.key];
      if (!original || !current) continue;
      const groupPatch: Record<string, number | boolean> = {};
      for (const field of group.fields) {
        if (original[field.key as keyof typeof original] !== current[field.key as keyof typeof current]) {
          groupPatch[field.key] = current[field.key as keyof typeof current] as number | boolean;
        }
      }
      if (Object.keys(groupPatch).length > 0) {
        patch[group.key] = groupPatch as never;
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.info('Нет изменений');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/admin-api/settings', patch);
      toast.success('Настройки сохранены');
      void invalidatePrefix(queryClient, '/admin-api/settings');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  if (query.isLoading && !draft) return <LoadingBlock label="Загрузка настроек…" />;
  if (query.error) return <ErrorBanner message={query.error.message} onRetry={() => query.refetch()} />;
  if (!draft) return null;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Настройки"
        description="Группы настроек системы. Изменения отправляются частями (deep merge)."
        actions={<Button variant="primary" loading={saving} onClick={save}>Сохранить</Button>}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        {GROUPS.map((group) => (
          <Card key={group.key} title={group.title}>
            <p className="mb-3 text-[11px] text-zinc-500">{group.hint}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {group.fields.map((field) => {
                const value = draft[group.key][field.key as keyof typeof draft[typeof group.key]];
                return (
                  <Field key={field.key} label={field.label}>
                    {field.type === 'boolean' ? (
                      <Toggle
                        checked={value as boolean}
                        onChange={(v) => setField(group.key, field.key, v)}
                      />
                    ) : field.type === 'micro' ? (
                      <div className="space-y-1">
                        <Input
                          value={String(value)}
                          onChange={(e) => setField(group.key, field.key, Number(e.target.value) || 0)}
                          inputMode="numeric"
                        />
                        <span className="text-[10px] text-zinc-600">≈ {money((value as number) / 1_000_000)}</span>
                      </div>
                    ) : (
                      <Input
                        value={String(value)}
                        onChange={(e) => setField(group.key, field.key, Number(e.target.value) || 0)}
                        inputMode="numeric"
                      />
                    )}
                  </Field>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
